import { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import withPermissionProtection from 'hoc/withPermissionProtection';

import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import SearchIcon from '@material-ui/icons/Search';
import ViewIcon from '@material-ui/icons/Visibility';
import DownloadIcon from '@material-ui/icons/CloudDownload';
import Typography from 'components/CncUI/Typography';
import TextField from 'components/CncUI/TextField';
import DataTable from 'components/CncUI/DataTable';
import Loader from 'components/Loader';
import FileViewer from 'components/FileViewer';
import FileDownloader from 'components/FileDownloader';

import useGetTaxDocuments from './hooks/useGetTaxDocuments';
import useGetTaxDocumentById from './hooks/useGetTaxDocumentById';
import filterDocuments from './utils/filterDocuments';
import Logger from 'utilities/logger';

const useStyles = makeStyles(({ breakpoints, palette }) => ({
  header: {
    marginTop: 40,
    marginBottom: 0,
    [breakpoints.up('lg')]: {
      fontSize: 48,
      lineHeight: '56px',
    },
  },
  description: {
    '& a': {
      color: palette.cncUI.misc.link,
    },
  },
  table: {
    marginTop: 8,
  },
  icon: {
    cursor: 'pointer',
  },
  link: {
    color: palette.cncUI.primary.gray['+0'],
  },
}));

const TaxDocuments = () => {
  const classes = useStyles();
  const [search, setSearch] = useState('');
  const [viewer, setViewer] = useState('');
  const [downloader, setDownloader] = useState('');
  const [documents, loading, error] = useGetTaxDocuments();
  const getTaxDocumentById = useGetTaxDocumentById();

  useEffect(() => {
    if (error) {
      Logger('taxDocuments').error(error);
    }
  }, [error]);

  const filtered = filterDocuments(documents, search);
  const cell = width => ({ style: { width: width } });
  const headerProps = { style: { width: 140, minWidth: 130 } };

  const columns = [
    { field: 'name', label: 'Document Type', cellProps: cell('25%') },
    { field: 'year', label: 'Document Year', headerProps },
    { field: 'employer', label: 'Employer' },
    {
      field: 'actions',
      label: ' ',
      sortable: false,
      cellProps: cell('15%'),
      value: doc => (
        <Grid container spacing={2} alignItems="center" justify="flex-end">
          <Grid item>
            <ViewIcon
              id={`ViewDocument-${doc.id}`}
              className={classes.icon}
              onClick={() => setViewer(doc.id)}
            />
          </Grid>
          <Grid item>
            <DownloadIcon
              id={`DownloadDocument-${doc.id}`}
              className={classes.icon}
              onClick={() => setDownloader(doc.id)}
            />
          </Grid>
        </Grid>
      ),
    },
  ];

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} id="Header">
        <Typography variant="h2" className={classes.header}>
          Tax Documents
        </Typography>
      </Grid>
      <Grid item xs={12} className={classes.description} id="Description">
        <Typography variant="body2" size="long">
          If you have any questions about your tax documents or are looking for
          tax documents not displayed, please contact our Tax Team at{' '}
          <Link href="mailto:payroll_tax@castandcrew.com">
            payroll_tax@castandcrew.com
          </Link>
          .
        </Typography>
      </Grid>
      <Grid item xs={12} sm={7} md={5} lg={3}>
        <TextField
          light
          fullWidth
          label="Filter tax documents"
          value={search}
          id="Filter"
          onChange={e => setSearch(e.target.value)}
          startIcon={<SearchIcon />}
        />
      </Grid>
      <Grid item xs={12} id="Table">
        {loading ? (
          <Loader message="Loading your documents..." />
        ) : (
          <>
            <DataTable
              transparent
              columns={columns}
              data={filtered}
              sort="year"
              order="desc"
              emptyRows="There are no tax documents to display"
              className={classes.table}
            />
            {viewer && (
              <FileViewer
                fileFetcher={getTaxDocumentById}
                config={{ id: viewer, isPreview: true }}
                onClose={() => setViewer('')}
                loaderMessage={'Hang tight while we fetch that tax document'}
              />
            )}
            {downloader && (
              <FileDownloader
                urlFetcher={getTaxDocumentById}
                config={{ id: downloader }}
                onClose={() => setDownloader('')}
              />
            )}
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default withPermissionProtection('tax_documents:read')(TaxDocuments);
